import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useStore, extractErrorMessage } from "../../../helpers";
import {
  buildSourceListItemFromUpload,
  invalidateReferenceTableSourceList,
  upsertSourceListItemInCache,
} from "./source-list-cache-helpers";
import { markTarsNotebookActivity } from "./tars-notebook-last-touched";

export interface UploadSourcePayload {
  notebook_id: number;
  type: string;
  parent_id: number | null;
  name: string;
  s3_key?: string;
  mime_type?: string;
  size_bytes?: number;
  tags?: string[];
  more?: Record<string, unknown>;
  onUploadSuccess?: () => void;
}

export interface UploadSourceResponse {
  id: number;
  name: string;
  parent_id: number | null;
  type: string;
  [key: string]: unknown;
}

export const useUploadSource = () => {
  const rootStore = useStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UploadSourcePayload): Promise<UploadSourceResponse> => {
      const body = {
        notebook_id: payload.notebook_id,
        type: payload.type,
        parent_id: payload.parent_id,
        name: payload.name,
        s3_key: payload.s3_key ?? "",
        mime_type: payload.mime_type ?? "",
        size_bytes: payload.size_bytes ?? 0,
        tags: payload.tags ?? [],
        more: payload.more ?? {},
      };
      const res = await rootStore.apiRequestHandler(
        "tars/source/upload",
        body,
        "Failed to add source"
      );
      return res.data as UploadSourceResponse;
    },
    onSuccess: (data, variables) => {
      toast.success("Source added successfully");
      markTarsNotebookActivity(queryClient, variables.notebook_id);
      const listItem = buildSourceListItemFromUpload(data, variables);
      upsertSourceListItemInCache(queryClient, variables.notebook_id, listItem);
      void invalidateReferenceTableSourceList(queryClient, variables.notebook_id);
      if (variables.parent_id != null) {
        queryClient.invalidateQueries({
          queryKey: ["tars", "source", "list", variables.notebook_id, "", variables.parent_id],
        });
      }
      variables.onUploadSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, "Failed to add source"));
    },
  });
};
